import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  IconDiscountCheck,
  IconUsers,
  IconCalendarRepeat,
  IconCheck,
  IconX,
  IconLoader2,
} from "@tabler/icons-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/cn";
import { getActivePlan, type AgencyPlan } from "../api";
import { PlanCatalogDialog } from "./PlanCatalogDialog";

const STATUS: Record<AgencyPlan["status"], { label: string; variant: "success" | "warning" | "danger" | "neutral" }> = {
  active: { label: "Ativo", variant: "success" },
  trial: { label: "Período de teste", variant: "warning" },
  past_due: { label: "Pagamento pendente", variant: "danger" },
  canceled: { label: "Cancelado", variant: "neutral" },
};

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

export function PlanTab() {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { data: plan, isLoading } = useQuery({
    queryKey: ["agency-plan"],
    queryFn: getActivePlan,
  });

  if (isLoading || !plan) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-ink-soft">
        <IconLoader2 className="animate-spin" size={20} /> Carregando plano…
      </div>
    );
  }

  const status = STATUS[plan.status];
  const seatPct = plan.seatsTotal > 0 ? Math.round((plan.seatsUsed / plan.seatsTotal) * 100) : 0;
  const cycleLabel = plan.billingCycle === "yearly" ? "/ano" : "/mês";

  return (
    <div className="grid gap-6">
      {/* Resumo do plano */}
      <section className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border bg-gradient-to-br from-brand-50 to-white p-6">
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-grad text-white shadow-glow">
              <IconDiscountCheck size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-ink">{plan.name}</h3>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>
              <p className="text-sm text-ink-muted">
                <span className="text-xl font-bold text-ink">{formatPrice(plan.priceCents)}</span>
                <span className="text-ink-soft"> {cycleLabel}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCatalogOpen(true)}
            className="rounded-lg border border-brand-600 bg-white px-4 py-2 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            Gerenciar plano
          </button>
        </div>

        <div className="grid gap-px bg-border sm:grid-cols-2">
          {/* Seats */}
          <div className="bg-white p-6">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-ink">
              <IconUsers size={18} className="text-brand-600" />
              Licenças de corretor
            </div>
            <p className="text-sm text-ink-muted">
              <span className="font-semibold text-ink">{plan.seatsUsed}</span> de {plan.seatsTotal} em uso
              <span className="text-ink-soft"> · {plan.seatsAvailable} disponíveis</span>
            </p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  seatPct >= 90 ? "bg-danger" : seatPct >= 70 ? "bg-warning" : "bg-brand-600",
                )}
                style={{ width: `${seatPct}%` }}
              />
            </div>
          </div>

          {/* Renovação */}
          <div className="bg-white p-6">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-ink">
              <IconCalendarRepeat size={18} className="text-brand-600" />
              Próxima renovação
            </div>
            <p className="text-sm text-ink-muted">
              Renova em <span className="font-semibold text-ink">{formatDate(plan.renewsAt)}</span>
            </p>
            <p className="mt-1 text-[12px] text-ink-soft">
              A cobrança é {plan.billingCycle === "yearly" ? "anual" : "mensal"} e renova automaticamente.
            </p>
          </div>
        </div>
      </section>

      {/* Recursos do plano */}
      <section className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <h3 className="mb-4 border-b border-border pb-2 text-lg font-semibold text-ink">
          O que está incluído
        </h3>
        <ul className="grid gap-3 sm:grid-cols-2">
          {plan.features.map((f) => (
            <li key={f.label} className="flex items-center gap-2.5 text-sm">
              <span
                className={cn(
                  "grid h-5 w-5 shrink-0 place-items-center rounded-full",
                  f.included ? "bg-success-soft text-success-ink" : "bg-gray-100 text-ink-soft",
                )}
              >
                {f.included ? <IconCheck size={13} /> : <IconX size={13} />}
              </span>
              <span className={cn(f.included ? "text-ink" : "text-ink-soft line-through")}>
                {f.label}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <PlanCatalogDialog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        currentCode={plan.code}
      />
    </div>
  );
}
